import { UserAPI as AccountAPI } from '../../user';
import { base64ToBytes, bytesToBase64 } from '../../util/base64';
import { argon2 } from '../argon2';

import type { ItemKeys } from './state/types';
import type { EncryptedVaultItem, VaultItemPolicy } from './api';
import { decryptMonolithicState, type StorageMigrator } from './migrations';
import {
  type Keychain,
  LATEST_KEYCHAIN_VERSION,
  type UserAPI,
  type VaultAPI,
  type VaultItem,
} from './types';

import { randomBytes, secretbox } from 'tweetnacl';

export * from './types';

function decryptVaultItem(auk: Uint8Array, encItem: EncryptedVaultItem): DecryptItemResult {
  const encData = base64ToBytes(encItem.encData);
  const nonce = base64ToBytes(encItem.encDataNonce);
  const encKey = base64ToBytes(encItem.encItemKey);
  const keyNonce = base64ToBytes(encItem.encItemKeyNonce);

  const key = secretbox.open(encKey, keyNonce, auk);
  if (!key) {
    throw new Error('failed to decrypt vault item key');
  }
  const data = secretbox.open(encData, nonce, key);
  if (!data) {
    throw new Error('failed to decrypt vault item');
  }

  return {
    item: {
      id: encItem.id,
      data: new TextDecoder().decode(data),
      // Pollen only receives VaultItem, not ItemKeys, so access policy must travel here.
      policy: encItem.policy,
    },
    keys: {
      ...(encItem.policy === 'read_only' ? {} : { full: key }),
      readOnly: encItem.policy === 'read_only' ? key : decryptReadOnlyItemKey(auk, encItem),
      policy: encItem.policy,
    },
  };
}

interface DecryptItemResult {
  item: VaultItem;
  keys: ItemKeys[string];
}

function decryptReadOnlyItemKey(auk: Uint8Array, encItem: EncryptedVaultItem): Uint8Array {
  if (!encItem.encReadOnlyItemKey || !encItem.encReadOnlyItemKeyNonce) {
    return randomBytes(32);
  }
  const key = secretbox.open(
    base64ToBytes(encItem.encReadOnlyItemKey),
    base64ToBytes(encItem.encReadOnlyItemKeyNonce),
    auk
  );
  if (!key) {
    throw new Error('failed to decrypt vault item read-only key');
  }
  return key;
}

export async function derivePasswordKey(password: string, salt: Uint8Array): Promise<Uint8Array> {
  const normalizedPassword = password.normalize('NFKC');
  return argon2Digest(normalizedPassword, salt);
}

async function argon2Digest(password: string | Uint8Array, salt: Uint8Array): Promise<Uint8Array> {
  // TODO: switch to argon2id
  let pw: Uint8Array;
  if (typeof password === 'string') {
    pw = new TextEncoder().encode(password);
  } else {
    pw = password;
  }
  return argon2({
    password: pw,
    salt,
    parallelism: 1,
    iterations: 1, // time
    memory: 16, // 2 ^ 16 == 65536 kibibytes == 64 mebibytes
    tagLength: 32,
    type: 'argon2d',
  });
}

export function encodeKeychain(keychain: Keychain): string {
  const { state } = keychain;

  const toJSON = (obj: any) =>
    JSON.stringify({
      ...obj,
      state,
      version: LATEST_KEYCHAIN_VERSION,
    });

  if (state === 'nostr') {
    return toJSON({
      pubkey: keychain.pubkey,
      auk: bytesToBase64(keychain.auk),
      email: keychain.email,
    });
  }
  if (state === 'passkeyOpen' || state === 'passkeyLocked') {
    return toJSON({
      auk: bytesToBase64(keychain.auk),
      email: keychain.email,
    });
  }
  if (state === 'passkeyNoVaultKey') {
    return toJSON({
      email: keychain.email,
    });
  }
  if (state === 'passwordOpen') {
    return toJSON({
      passwordKey: bytesToBase64(keychain.passwordKey),
      secretKey: bytesToBase64(keychain.secretKey),
      email: keychain.email,
    });
  }
  if (state === 'passwordLocked') {
    return toJSON({
      secretKey: bytesToBase64(keychain.secretKey),
      email: keychain.email,
    });
  }
  if (state === 'passwordNoSecretKey') {
    return toJSON({
      email: keychain.email,
      passwordKey: bytesToBase64(keychain.passwordKey),
    });
  }
  throw new Error(`unreachable: unknown keychain state: ${state}`);
}

export async function putVaultItem(
  vaultAPI: VaultAPI,
  itemKeys: ItemKeys,
  auk: Uint8Array,
  item: VaultItem
): Promise<void> {
  const data = new TextEncoder().encode(item.data);
  const readOnlyData = new TextEncoder().encode(item.readOnlyData ?? item.data);

  if (itemKeys[item.id]) {
    const keys = itemKeys[item.id];
    if (!keys.full) {
      throw new Error('cannot update a read-only vault item');
    }
    const nonce = randomBytes(24);
    const readOnlyNonce = randomBytes(24);
    const encData = secretbox(data, nonce, keys.full);
    const encReadOnlyData = secretbox(readOnlyData, readOnlyNonce, keys.readOnly);
    const readOnlyKeyNonce = randomBytes(24);
    const encReadOnlyItemKey = secretbox(keys.readOnly, readOnlyKeyNonce, auk);

    await vaultAPI.updateItem({
      id: item.id,
      encDataNonce: bytesToBase64(nonce),
      encData: bytesToBase64(encData),
      encReadOnlyData: bytesToBase64(encReadOnlyData),
      encReadOnlyDataNonce: bytesToBase64(readOnlyNonce),
      encReadOnlyItemKey: bytesToBase64(encReadOnlyItemKey),
      encReadOnlyItemKeyNonce: bytesToBase64(readOnlyKeyNonce),
    });
  } else {
    const key = randomBytes(32);
    const readOnlyKey = randomBytes(32);
    const keyNonce = randomBytes(24);
    const readOnlyKeyNonce = randomBytes(24);
    const nonce = randomBytes(24);
    const readOnlyNonce = randomBytes(24);
    const encData = secretbox(data, nonce, key);
    const encReadOnlyData = secretbox(readOnlyData, readOnlyNonce, readOnlyKey);
    const encKey = secretbox(key, keyNonce, auk);
    const encReadOnlyKey = secretbox(readOnlyKey, readOnlyKeyNonce, auk);

    await vaultAPI.createItem({
      id: item.id,
      encData: bytesToBase64(encData),
      encDataNonce: bytesToBase64(nonce),
      encReadOnlyData: bytesToBase64(encReadOnlyData),
      encReadOnlyDataNonce: bytesToBase64(readOnlyNonce),
      encItemKey: bytesToBase64(encKey),
      encItemKeyNonce: bytesToBase64(keyNonce),
      encReadOnlyItemKey: bytesToBase64(encReadOnlyKey),
      encReadOnlyItemKeyNonce: bytesToBase64(readOnlyKeyNonce),
    });
    itemKeys[item.id] = { full: key, readOnly: readOnlyKey, policy: 'owner' };
  }
}

export async function getVaultItems(
  vaultAPI: VaultAPI,
  auk: Uint8Array,
  email: string
): Promise<[VaultItem[], ItemKeys]> {
  const encItems = await vaultAPI.listItems(email);
  const itemKeys = {};
  const items: VaultItem[] = [];

  for (let i = 0; i < encItems.length; i++) {
    const result = decryptVaultItem(auk, encItems[i]);
    items.push(result.item);
    itemKeys[result.item.id] = result.keys;
  }

  return [items, itemKeys];
}

export async function deriveBits(shareKey: Uint8Array, info: string): Promise<Uint8Array> {
  const { subtle } = window.crypto;
  const subtleShareKey = await subtle.importKey('raw', shareKey as BufferSource, 'HKDF', false, [
    'deriveBits',
  ]);

  const shareToken = await subtle.deriveBits(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      salt: new Uint8Array(32),
      info: new TextEncoder().encode(info),
    },
    subtleShareKey,
    256
  );
  return new Uint8Array(shareToken);
}

export async function createItems(
  vaultAPI: VaultAPI,
  items: VaultItem[],
  auk: Uint8Array
): Promise<ItemKeys> {
  const itemKeys = {};
  await Promise.all(
    items.map(async (item) => {
      await putVaultItem(vaultAPI, itemKeys, auk, item);
    })
  );
  return itemKeys;
}

export async function migrateMonolithicState(
  encryptedState: string,
  auk: Uint8Array,
  userAPI: UserAPI,
  vaultAPI: VaultAPI,
  migrator: StorageMigrator
): Promise<[VaultItem[], ItemKeys]> {
  const state = decryptMonolithicState(encryptedState, auk);
  const items = migrator.migrateState(state);
  const itemKeys: ItemKeys = {};
  await Promise.all(items.map((item) => putVaultItem(vaultAPI, itemKeys, auk, item)));
  await userAPI.removeMonolithicState();
  return [items, itemKeys];
}

export async function shareItem(
  vaultAPI: VaultAPI,
  auk: Uint8Array,
  itemID: string,
  itemKeys: ItemKeys,
  extraParams: string,
  policy: Exclude<VaultItemPolicy, 'owner'>,
  localSsoFqdn?: string
): Promise<string> {
  const keys = itemKeys[itemID];
  if (!keys) {
    throw new Error(`cannot share unknown item: ${itemID}`);
  }
  if (
    keys.policy !== 'owner' &&
    !(localSsoFqdn && keys.policy === 'editor' && policy === 'read_only')
  ) {
    throw new Error('only vault item owners can create share links');
  }
  const itemKey = policy === 'editor' ? keys.full : keys.readOnly;
  if (!itemKey) {
    throw new Error('vault item is missing its full-access key');
  }

  const shareSecret = randomBytes(32);
  const shareToken = await deriveBits(shareSecret, 'clovyr.share-token');
  const shareKey = await deriveBits(shareSecret, 'clovyr.share-key');

  const itemNonce = randomBytes(24);
  const encItemKey = secretbox(itemKey, itemNonce, shareKey);

  if (localSsoFqdn) {
    if (policy !== 'read_only') throw new Error('SSO invites require read-only library access');
    const { token } = await new AccountAPI().createLocalSsoInvite(localSsoFqdn, {
      appID: 'clovyr-ai-cloud',
      itemID,
      encItemKey: bytesToBase64(encItemKey),
      encItemKeyNonce: bytesToBase64(itemNonce),
    });
    const url = new URL('/auth/local-sso-invite', window.location.origin);
    url.hash = new URLSearchParams({ token, key: bytesToBase64(shareSecret) }).toString();
    return url.toString();
  }

  let encReadOnlyItemKey: string | undefined;
  let encReadOnlyItemKeyNonce: string | undefined;
  if (policy === 'editor') {
    const readOnlyNonce = randomBytes(24);
    encReadOnlyItemKey = bytesToBase64(secretbox(keys.readOnly, readOnlyNonce, shareKey));
    encReadOnlyItemKeyNonce = bytesToBase64(readOnlyNonce);
  }

  const shareSecretNonce = randomBytes(24);
  const encShareSecret = secretbox(shareSecret, shareSecretNonce, auk);

  const shareTokenBase64 = bytesToBase64(shareToken);
  const resp = await vaultAPI.shareItem({
    id: itemID,
    encShareSecret: bytesToBase64(encShareSecret),
    encShareSecretNonce: bytesToBase64(shareSecretNonce),
    shareToken: shareTokenBase64,
    encItemKey: bytesToBase64(encItemKey),
    encItemKeyNonce: bytesToBase64(itemNonce),
    encReadOnlyItemKey,
    encReadOnlyItemKeyNonce,
    policy,
  });

  if (!resp) {
    return `${window.location.origin}/library/accept-share/${itemID}?${extraParams}#${bytesToBase64(shareSecret)}`;
  }

  const existingEncShareKey = base64ToBytes(resp.encShareKey);
  const existingShareNonce = base64ToBytes(resp.encShareKeyNonce);
  const existingShareKey = secretbox.open(existingEncShareKey, existingShareNonce, auk);
  if (!existingShareKey) {
    throw new Error('share link already exists but we cannot decrypt the share secret');
  }

  return `${window.location.origin}/library/accept-share/${itemID}?${extraParams}#${bytesToBase64(existingShareKey)}`;
}

export async function acceptShare(
  vaultAPI: VaultAPI,
  auk: Uint8Array,
  itemID: string,
  shareSecret: string,
  email: string,
  localSsoToken?: string
): Promise<[VaultItem[], ItemKeys]> {
  const shareSecretBytes = base64ToBytes(shareSecret);
  const shareToken = bytesToBase64(await deriveBits(shareSecretBytes, 'clovyr.share-token'));
  const shareKey = await deriveBits(shareSecretBytes, 'clovyr.share-key');

  const resp = localSsoToken
    ? await new AccountAPI().getLocalSsoInviteLibrary(localSsoToken)
    : await vaultAPI.getItemShare({ id: itemID, shareToken });
  const encItemKey = base64ToBytes(resp.encItemKey);
  const itemNonce = base64ToBytes(resp.encItemKeyNonce);
  const itemKey = secretbox.open(encItemKey, itemNonce, shareKey);
  if (!itemKey) {
    throw new Error('cannot accept share: failed to decrypt item key');
  }

  if (localSsoToken) {
    const data = secretbox.open(
      base64ToBytes(resp.encData),
      base64ToBytes(resp.encDataNonce),
      itemKey
    );
    if (!data) throw new Error('cannot decrypt invited application');
    const item = JSON.parse(new TextDecoder().decode(data));
    if (
      resp.policy !== 'read_only' ||
      item.kind !== 'deployment' ||
      item.value?.appID !== 'clovyr-ai-cloud' ||
      !('deploymentFqdn' in resp) ||
      item.value?.fqdn !== resp.deploymentFqdn
    ) {
      throw new Error('SSO library invitations are only supported for AI Cloud');
    }
  }

  const myItemNonce = randomBytes(24);
  const myEncItemKey = secretbox(itemKey, myItemNonce, auk);

  let myEncReadOnlyItemKey: string | undefined;
  let myEncReadOnlyItemKeyNonce: string | undefined;
  if (resp.policy === 'editor') {
    const readOnlyItemKey =
      resp.encReadOnlyItemKey && resp.encReadOnlyItemKeyNonce
        ? secretbox.open(
            base64ToBytes(resp.encReadOnlyItemKey),
            base64ToBytes(resp.encReadOnlyItemKeyNonce),
            shareKey
          )
        : randomBytes(32);
    if (!readOnlyItemKey) {
      throw new Error('cannot accept editor share: failed to decrypt read-only item key');
    }
    const nonce = randomBytes(24);
    myEncReadOnlyItemKey = bytesToBase64(secretbox(readOnlyItemKey, nonce, auk));
    myEncReadOnlyItemKeyNonce = bytesToBase64(nonce);
  }

  const acceptance = {
    id: itemID,
    shareToken,
    encItemKey: bytesToBase64(myEncItemKey),
    encItemKeyNonce: bytesToBase64(myItemNonce),
    encReadOnlyItemKey: myEncReadOnlyItemKey,
    encReadOnlyItemKeyNonce: myEncReadOnlyItemKeyNonce,
  };
  if (localSsoToken) {
    await new AccountAPI().redeemLocalSsoInvite(localSsoToken, {
      encItemKey: acceptance.encItemKey,
      encItemKeyNonce: acceptance.encItemKeyNonce,
    });
  } else {
    await vaultAPI.acceptShare(acceptance);
  }

  // return all items we now have access to (not just the new ones)
  return getVaultItems(vaultAPI, auk, email);
}
